import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useQueryParams } from '@main/core-ui';

import {
  selectCurrentPartition,
  selectRequiredPrivacyCenter,
} from '../containers/App/selectors';
import { setPartition } from '../containers/App/slice';
import { globalSettingsMessages } from '../pages/ManageYourPrivacy/components/GlobalSettings/messages';
/**
 * Returns the currently selected partition key, either pulled from the URL or taking the default consent manager.
 *
 * TODO: https://linear.app/transcend/issue/WAR-3477 - In future, we may want to cache this to local storage
 *
 * @returns The currently selected partition key, and a function to set the partition key
 */
export function usePartition(): [
  {
    /** The currently selected partition */
    selectedPartition?: string;
    /** The set of partitions that are available to select against */
    partitionOptions: {
      /** Name of partition */
      label: string;
      /** Partition */
      value: string;
    }[];
    /** The currently selected partition */
    selectedPartitionOption?: {
      /** Name of partition */
      label: string;
      /** Partition */
      value: string;
    };
    /** Whether the partition is allowed to change */
    allowPartitionChange: boolean;
    /** Whether the partition should be shown */
    showPartition: boolean;
  },
  (partition: string) => void,
] {
  const { formatMessage } = useIntl();
  // redux
  const dispatch = useDispatch();
  const {
    consentManagers,
    consentPartitions = [],
    allowPartitionChange,
    showPartition,
  } = useSelector(selectRequiredPrivacyCenter);
  const selectedPartition = useSelector(selectCurrentPartition);

  // query param takes priority
  const { partition } = useQueryParams();
  useEffect(() => {
    if (partition !== selectedPartition && partition) {
      dispatch(setPartition({ partition }));
    }
  }, [partition, selectedPartition, dispatch]);

  // The full set of partition options
  const partitionOptions = useMemo(
    () => [
      ...(consentManagers[0]?.id
        ? [
            {
              label: formatMessage(globalSettingsMessages.globalPreferences),
              value: consentManagers[0].id,
            },
          ]
        : []),
      ...(consentPartitions || []).map((partition) => ({
        label: partition.name,
        value: partition.partition,
      })),
    ],
    [consentManagers, consentPartitions, formatMessage],
  );

  // construct the partition options
  const selectedPartitionOption = useMemo(
    () => partitionOptions.find((part) => part.value === selectedPartition),
    [selectedPartition, partitionOptions],
  );

  return [
    {
      selectedPartition: partition || selectedPartition,
      partitionOptions,
      selectedPartitionOption,
      allowPartitionChange,
      showPartition,
    },
    (newPartition) => dispatch(setPartition({ partition: newPartition })),
  ];
}
